/**
 * Only public, versioned build assets may be cached by the worker.
 * Navigation and all PocketBase / finance API traffic remain network-only.
 */
export const PWA_UPDATE_EVENT = 'invoiceflow:pwa-update';

let pendingUpdate: ServiceWorkerRegistration | null = null;
let shouldReload = false;
let initialized = false;
let lastUpdateCheck = 0;

const showWaitingUpdate = (registration: ServiceWorkerRegistration) => {
  if (!registration.waiting || !navigator.serviceWorker.controller) return;
  pendingUpdate = registration;
  window.dispatchEvent(new Event(PWA_UPDATE_EVENT));
};

export const getPendingPwaUpdate = (): ServiceWorkerRegistration | null => pendingUpdate;

export const applyPendingPwaUpdate = (): void => {
  if (!pendingUpdate?.waiting) return;
  shouldReload = true;
  pendingUpdate.waiting.postMessage({ type: 'SKIP_WAITING' });
};

export const registerPwaServiceWorker = (): void => {
  if (initialized || !import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  initialized = true;

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!shouldReload) return;
    shouldReload = false;
    window.location.reload();
  });

  const register = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none'
      });
      showWaitingUpdate(registration);

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        installing?.addEventListener('statechange', () => {
          if (installing.state === 'installed') showWaitingUpdate(registration);
        });
      });

      const checkForUpdate = () => {
        if (!navigator.onLine || document.visibilityState !== 'visible') return;
        const now = Date.now();
        if (now - lastUpdateCheck < 60 * 60 * 1000) return;
        lastUpdateCheck = now;
        void registration.update().catch(() => { /* Respect offline sessions. */ });
      };

      window.addEventListener('focus', checkForUpdate);
      document.addEventListener('visibilitychange', checkForUpdate);
    } catch (error) {
      console.warn('InvoiceFlow install support is unavailable:', error);
    }
  };

  if (document.readyState === 'complete') void register();
  else window.addEventListener('load', () => { void register(); }, { once: true });
};